import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import {
  APP_PREFIX_PATH,
  AUTH_PREFIX_PATH,
  REDIRECT_URL_KEY,
  UNAUTHENTICATED_ENTRY,
} from "configs/AppConfig";
import { getUser } from "store/slices/authSlice";
import { hasCatalogAccess } from "utils/accessControl";
import { TermsAndConditionsModal } from "../components/shared-components/TermsAndConditionsModal";
import { DepartmentModal, needsProfileSurvey } from "../components/shared-components/DepartmentModal";
import Loading from "../components/shared-components/Loading";
import OnboardingTour from "../components/onboarding-tour/OnboardingTour";

const ONBOARDING_PATH = `${APP_PREFIX_PATH}/onboarding/brand-details`;

const ProtectedRoute = ({ getUser, user }) => {
  const { token } = useSelector((state) => state.auth);
  const location = useLocation();

  if (!token) {
    const fullPath = `${location.pathname}${location.search}`;
    const redirectTo = `${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}?${REDIRECT_URL_KEY}=${encodeURIComponent(fullPath)}`;
    return (
      <Navigate
        to={redirectTo}
        replace
      />
    );
  }

  // Wait for user to load
  if (!user) {
    return <Loading cover="page" />;
  }

  const isOnboardingPage = location.pathname.startsWith(`${APP_PREFIX_PATH}/onboarding`);

  // Redirect to onboarding if not complete (and not already on onboarding page)
  if (!user.current_company?.is_onboarding_complete && user.current_company_id && !isOnboardingPage) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  // Redirect away from onboarding if already complete. New accounts land in the
  // catalog straight after onboarding (if they have catalog access), instead of
  // the brand-store default entry.
  if (user.current_company?.is_onboarding_complete && isOnboardingPage) {
    const postOnboarding = hasCatalogAccess(user)
      ? `${APP_PREFIX_PATH}/catalog`
      : APP_PREFIX_PATH;
    return <Navigate to={postOnboarding} replace />;
  }

  return (
    <>
      {!user.has_agreed_to_privacy_policy && <TermsAndConditionsModal isOpen />}
      {/* Department survey waits until AFTER onboarding (the "Generating your brand
          store" step) — it must not pop over the onboarding flow. It shows once the
          new user lands on the catalog. */}
      {user.has_agreed_to_privacy_policy && needsProfileSurvey(user) && !isOnboardingPage && <DepartmentModal isOpen />}
      <OnboardingTour />
      <Outlet />
    </>
  );
};

const mapStateToProps = ({ auth }) => {
  const { user } = auth;

  return { user };
};

const mapDispatchToProps = {
  getUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProtectedRoute);
